import React from "react";
import PropTypes from "prop-types";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";

import { status, marks } from "../../theme/charts";

import { AreaChart, Area, ResponsiveContainer } from "recharts";

import ArrowUpIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownIcon from "@material-ui/icons/ArrowDownward";
import RemoveIcon from "@material-ui/icons/Remove";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    position: "relative",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    letterSpacing: "0.01em",
  },
  icon: { color: theme.palette.text.disabled, fontSize: 18 },
  value: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.15,
    fontVariantNumeric: "tabular-nums",
    color: theme.palette.text.primary,
  },
  delta: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontVariantNumeric: "tabular-nums",
  },
  deltaIcon: { fontSize: 14 },
  up: { color: theme.palette.success.main },
  down: { color: theme.palette.error.main },
  flat: { color: theme.palette.text.secondary },
  vs: { color: theme.palette.text.secondary },
  hint: { fontSize: 12, color: theme.palette.text.secondary },
  // O minigráfico mora atrás do conteúdo, encostado na base do cartão: é
  // contexto periférico, não informação a ser lida ponto a ponto.
  spark: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 40,
    opacity: 0.5,
    pointerEvents: "none",
  },
  content: { position: "relative", zIndex: 1 },
}));

/**
 * Cartão de indicador com comparação.
 *
 * Um número sozinho quase não informa: "342 oportunidades" só vira decisão
 * quando se sabe se são mais ou menos que antes. Por isso a variação faz parte
 * do cartão, e não é um extra opcional.
 *
 * Quando não há período anterior para comparar, mostra "sem base de comparação"
 * em vez de inventar um percentual. Sair de zero para dez não é crescimento de
 * 100% — é um começo, e fingir precisão aí faria a tela mentir.
 */
const KpiCard = ({ label, value, icon: Icon, delta, hint, invertColors, series }) => {
  const classes = useStyles();
  // Um id por instância: dois gradientes com o mesmo id fazem o segundo cartão
  // herdar a cor do primeiro.
  const gradientId = React.useMemo(
    () => Math.random().toString(36).slice(2, 9),
    []
  );

  const renderDelta = () => {
    if (!delta) return hint ? <div className={classes.hint}>{hint}</div> : null;

    const { pct, direction, previousLabel } = delta;

    if (pct === null || pct === undefined) {
      return (
        <Tooltip title="Não houve movimento no período anterior para comparar">
          <div className={`${classes.delta} ${classes.flat}`}>
            <RemoveIcon className={classes.deltaIcon} />
            <span>sem base de comparação</span>
          </div>
        </Tooltip>
      );
    }

    // Em algumas métricas subir é ruim — despesa, tickets em atraso, perdas.
    // A cor segue o significado, não a direção da seta.
    const bom = invertColors ? direction === "down" : direction === "up";
    const cor =
      direction === "flat" ? classes.flat : bom ? classes.up : classes.down;

    const Seta =
      direction === "up" ? ArrowUpIcon : direction === "down" ? ArrowDownIcon : RemoveIcon;

    return (
      <div className={`${classes.delta} ${cor}`}>
        <Seta className={classes.deltaIcon} />
        <span>
          {pct > 0 ? "+" : ""}
          {Number(pct).toFixed(1)}%
        </span>
        <span className={classes.vs}>{previousLabel || "vs período anterior"}</span>
      </div>
    );
  };

  // Só desenha a linha com pelo menos dois pontos: um ponto sozinho vira um
  // traço reto que sugere estabilidade onde não há série nenhuma.
  const temSerie = Array.isArray(series) && series.length >= 2;
  const corLinha = delta?.direction === "down"
    ? (invertColors ? status.good : status.critical)
    : (invertColors ? status.critical : status.good);

  return (
    <Paper variant="outlined" className={classes.root}>
      {temSerie && (
        <div className={classes.spark} aria-hidden="true">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id={`spark-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={corLinha} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={corLinha} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={corLinha}
                strokeWidth={marks.lineWidth}
                fill={`url(#spark-${gradientId})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className={classes.content}>
        <div className={classes.head}>
          <span className={classes.label}>{label}</span>
          {Icon && <Icon className={classes.icon} />}
        </div>
        <div className={classes.value}>{value}</div>
        {renderDelta()}
      </div>
    </Paper>
  );
};

KpiCard.propTypes = {
  label: PropTypes.node.isRequired,
  value: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  /** Bloco `deltas` do backend: { pct, direction, current, previous }. */
  delta: PropTypes.shape({
    pct: PropTypes.number,
    direction: PropTypes.oneOf(["up", "down", "flat"]),
    previousLabel: PropTypes.string,
  }),
  /** Texto auxiliar quando não há comparação. */
  hint: PropTypes.node,
  /** Verdadeiro quando subir é ruim (despesa, perdas, atrasos). */
  invertColors: PropTypes.bool,
  /** Série do período para o minigráfico: [{ value }]. */
  series: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number })),
};

KpiCard.defaultProps = {
  icon: undefined,
  delta: undefined,
  hint: undefined,
  invertColors: false,
  series: undefined,
};

export default KpiCard;
